import { useEffect, useMemo, useState } from "react";
import { Edit2, Trash2, Check } from "lucide-react";
import { groupService } from "../../../../services/admin/groupService.js";
import ConfirmDeleteModal from "../users/ConfirmDeleteModal.jsx";
import Pagination from "../../../common/Pagination.jsx";
import { SearchBar, EmptyState } from "../../index.jsx";

export default function RoleTable({ onEdit }) {
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // ✅ Single toast state replaces old showToast boolean
  const [toast, setToast] = useState({ visible: false, message: "", type: "success" });

  const showToast = (message, type = "success") => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast({ visible: false, message: "", type: "success" }), 1500);
  };

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      const response = await groupService.getAllGroups();
      setRoles(response || []);
    } catch (error) {
      console.error("Error loading roles", error);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await groupService.delete(selectedRole.groupId);
      setShowDeleteModal(false);
      loadRoles();
      showToast("Role deleted successfully", "success"); // ✅ delete success
    } catch (error) {
      console.error("Delete error", error);
      setShowDeleteModal(false);
      showToast("Failed to delete role. Please try again.", "error"); // ✅ delete error
    }
  };

  const filteredRoles = useMemo(
    () =>
      roles.filter((role) =>
        String(role.roleName || "").toLowerCase().includes(search.toLowerCase())
      ),
    [roles, search]
  );

  const totalPages = Math.ceil(filteredRoles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentRecords = filteredRoles.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, itemsPerPage]);

  return (
    <div className="bg-white dark:bg-slate-800 rounded-[2rem] shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500 flex flex-col h-auto max-h-[calc(100vh-140px)]">

      {/* Header */}
      <div className="p-5 border-b border-slate-100 dark:border-slate-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shrink-0">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-white ml-2">
          Role Directory
        </h3>
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
          />
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search roles..."
          />
        </div>
      </div>

      {/* Table */}
      <div className="w-full flex-1 overflow-auto hide-scrollbar">
        <table className="w-full text-left border-collapse min-w-[800px]">
          <thead>
            <tr className="bg-slate-50/50 dark:bg-slate-700/50 border-b border-slate-100 dark:border-slate-700 text-slate-500 dark:text-slate-400 text-sm">
              <th className="px-6 py-4 font-semibold whitespace-nowrap">Sr.No</th>
              <th className="px-6 py-4 font-semibold whitespace-nowrap">Role Name</th>
              <th className="px-6 py-4 font-semibold whitespace-nowrap">Default Page</th>
              <th className="px-6 py-4 font-semibold text-center whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {currentRecords.length === 0 ? (
              <tr>
                <td colSpan={4}>
                  <EmptyState query={search} label="roles" />
                </td>
              </tr>
            ) : (
              currentRecords.map((role, index) => (
                <tr
                  key={role.id}
                  className="hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition-colors group"
                >
                  <td className="px-6 py-3 text-slate-700 dark:text-slate-300">
                    {startIndex + index + 1}
                  </td>
                  <td className="px-6 py-3 font-semibold text-slate-800 dark:text-slate-200">
                    {role.roleName}
                  </td>
                  <td className="px-6 py-3 text-slate-600 dark:text-slate-400">
                    {role.defaultPage}
                  </td>
                  <td className="px-6 py-3 text-center">
                    <div className="flex items-center justify-center">
                      <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-1 shadow-sm">
                        <button
                          className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                          onClick={() => onEdit(role)}
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg transition-colors"
                          onClick={() => {
                            setSelectedRole(role);
                            setShowDeleteModal(true);
                          }}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Delete Modal */}
      {selectedRole && (
        <ConfirmDeleteModal
          module={'Role'}
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
          userName={selectedRole.roleName}
        />
      )}

      {/* ✅ Toast — green for success, red for error, no new imports needed */}
      {toast.visible && (
        <div className="fixed top-6 right-6 z-[9999] bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300">
          <div
            className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
              toast.type === "success" ? "bg-[#36a852]" : "bg-red-500"
            }`}
          >
            {toast.type === "success" ? (
              <Check size={14} strokeWidth={3} />
            ) : (
              <span style={{ fontSize: "14px", fontWeight: "bold", lineHeight: 1 }}>✕</span>
            )}
          </div>
          <span className="text-sm font-semibold">{toast.message}</span>
        </div>
      )}
    </div>
  );
}
